import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Anchor, Eye, EyeOff, CheckCircle2 } from 'lucide-react'
import { useAuth } from '../../core/auth'

const POST_SIGNUP_PATH = '/lng-ops'

const VALUE_PROPS = [
  'Per-voyage warranty deltas against charter-party guarantees',
  'LNGe, FOE, and steam BOR methodologies out of the box',
  'Live fleet map with route history and destination resolution',
  'Audit-ready Excel and PDF reports in one click',
]

export default function Signup() {
  const navigate = useNavigate()
  const { user, signup } = useAuth()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (user) navigate(POST_SIGNUP_PATH, { replace: true })
  }, [user, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)
    try {
      await signup(email.trim(), name.trim(), companyName.trim(), password)
      navigate(POST_SIGNUP_PATH)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Signup failed'
      if (msg.toLowerCase().includes('already')) {
        setError('That email is already registered. Sign in instead?')
      } else {
        setError(msg)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen bg-bg-canvas">
      <SignupBrandingPanel />
      <FormPanel>
        <SignupForm
          email={email}
          name={name}
          companyName={companyName}
          password={password}
          showPassword={showPassword}
          error={error}
          loading={loading}
          onEmailChange={setEmail}
          onNameChange={setName}
          onCompanyNameChange={setCompanyName}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword((v) => !v)}
          onSubmit={handleSubmit}
        />
      </FormPanel>
    </div>
  )
}

function SignupBrandingPanel() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden p-10 lg:flex lg:w-3/5">
      {/* Subtle gradient backdrop */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 20% 30%, rgba(209,176,122,0.07) 0%, transparent 60%), ' +
            'radial-gradient(ellipse at 80% 80%, rgba(209,176,122,0.04) 0%, transparent 50%)',
        }}
      />
      {/* Grid texture overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'linear-gradient(rgba(209,176,122,0.04) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(209,176,122,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-muted">
          <Anchor className="h-4.5 w-4.5 text-accent" />
        </div>
        <span className="font-display text-lg font-semibold">LNG Performance</span>
      </div>

      <div className="relative max-w-sm">
        <h2 className="font-display text-3xl font-semibold leading-snug text-text-primary">
          Your free trial includes everything
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-text-secondary">
          Demo vessels are pre-loaded so you can explore the full platform
          without importing any data. No credit card required.
        </p>

        <ul className="mt-8 flex flex-col gap-3.5">
          {VALUE_PROPS.map((prop) => (
            <li key={prop} className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-positive" />
              <span className="text-sm text-text-secondary">{prop}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs text-text-muted">
        &copy; {new Date().getFullYear()} LNG Performance &middot; Voyage Performance Intelligence
      </p>
    </div>
  )
}

function FormPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex w-full flex-col items-center justify-center border-l border-border bg-bg-surface px-8 py-12 lg:w-2/5">
      {/* Mobile logo */}
      <div className="mb-8 flex flex-col items-center lg:hidden">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-muted">
          <Anchor className="h-5 w-5 text-accent" />
        </div>
        <span className="mt-3 font-display text-xl font-semibold text-text-primary">LNG Performance</span>
      </div>
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

interface SignupFormProps {
  email: string
  name: string
  companyName: string
  password: string
  showPassword: boolean
  error: string
  loading: boolean
  onEmailChange: (v: string) => void
  onNameChange: (v: string) => void
  onCompanyNameChange: (v: string) => void
  onPasswordChange: (v: string) => void
  onTogglePassword: () => void
  onSubmit: (e: React.FormEvent) => void
}

function SignupForm({
  email,
  name,
  companyName,
  password,
  showPassword,
  error,
  loading,
  onEmailChange,
  onNameChange,
  onCompanyNameChange,
  onPasswordChange,
  onTogglePassword,
  onSubmit,
}: SignupFormProps) {
  return (
    <div>
      <div className="mb-7">
        <h1 className="font-display text-2xl font-semibold text-text-primary">Start your free trial</h1>
        <p className="mt-1.5 text-sm text-text-muted">Instant access · No credit card required</p>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label className="hero-label mb-1.5 block">Work email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@company.com"
            className="input h-9 w-full"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="hero-label mb-1.5 block">Full name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Jane Smith"
            className="input h-9 w-full"
            required
          />
        </div>

        <div>
          <label className="hero-label mb-1.5 block">Company</label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => onCompanyNameChange(e.target.value)}
            placeholder="Acme Shipping Ltd"
            className="input h-9 w-full"
            required
          />
        </div>

        <div>
          <label className="hero-label mb-1.5 block">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              placeholder="At least 8 characters"
              className="input h-9 w-full pr-10"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {error && <p className="text-xs text-negative">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary mt-1 h-10 w-full"
        >
          {loading ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="mt-6 text-center text-xs text-text-muted">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-accent hover:text-accent-hover">
          Sign in
        </Link>
      </p>
    </div>
  )
}
